/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useEffect } from 'react';

// Mantine
import { Group, Box, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';

// Components

import {
	PasswordInput,
	Text,
	Button,
	TextInput,
	Title,
} from '@components/v2/ui';

// Fetch data
import { useResetPassword } from '@features/update-password/api/reset-password';

// I18n
import { useTranslation } from 'react-i18next';

// Router
import { useLocation, useNavigate } from 'react-router';

// Types
import { ResetPasswordData } from '@features/update-password/types';

// Icons
import { IconInfoCircle } from '@tabler/icons-react';

import { FormWrapper } from '@components/v2/ui/public/FormWrapper';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type ResetPasswordProps = {
	email: string;
	token: string;
	isValidToken: boolean;
	isTokenExpired: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ResetPassword = (
	props: ResetPasswordProps
) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const icon = <IconInfoCircle />;

	const form = useForm({
		initialValues: {
			email: props.email,
			password: '',
			confirmPassword: '',
		},
		validate: {
			password: (value) =>
				value.length < 8
					? t('form.error.passwordMustHaveEightLetters')
					: null,
			confirmPassword: (value, values) =>
				value !== values.password
					? t('form.error.passwordsDidNotMatch')
					: null,
		},
	});

	const resetPassword = useResetPassword();

	useEffect(() => {
		if (resetPassword.isSuccess) {
			form.reset();
		}
	}, [resetPassword.isSuccess]);

	/* ******************************** RENDERING ******************************* */
	return (
		<form
			onSubmit={form.onSubmit((values) => {
				const payload: ResetPasswordData = {
					password: values.password,
					token: props.token ? props.token : '',
				};
				resetPassword.mutate(payload);
			})}
		>
			<FormWrapper>
				{location.pathname == '/auth/sign_in' ? (
					<Box>
						<Title ta="center" c={'#1D3965'} order={5}>
							{t('resetPassword.choosePassword')}
						</Title>
					</Box>
				) : (
					<Box>
						<Title ta="center" c={'#1D3965'} order={5}>
							{t('resetPassword.title')}
						</Title>
					</Box>
				)}

				<Text ta="center" c={'#9BA6B7'} variant="body1">
					{t('resetPassword.description')}
				</Text>
				{props.isValidToken ? (
					!props.isTokenExpired ? (
						<>
							<TextInput
								mt={'24px'}
								disabled
								placeholder={t('auth.yourEmailAddress')}
								label={t('auth.yourEmailAddress')}
								{...form.getInputProps('email')}
							/>
							<PasswordInput
								mt={'6px'}
								placeholder={t(
									'resetPassword.newPassword'
								)}
								label={t('resetPassword.newPassword')}
								{...form.getInputProps('password')}
							/>
							<PasswordInput
								mt={'6px'}
								placeholder={t(
									'resetPassword.confirmNewPassword'
								)}
								label={t(
									'resetPassword.confirmNewPassword'
								)}
								{...form.getInputProps('confirmPassword')}
							/>
							<Button
								mt={'24px'}
								variant="primary"
								fullWidth
								type="submit"
								loading={
									resetPassword && resetPassword.isLoading
								}
							>
								{t('global.submit')}
							</Button>
							<Group justify="center" mt={10}>
								<Button
									variant="transparent"
									onClick={() => {
										navigate('/login');
									}}
									color="blue"
									size="h6"
								>
									{t('global.return')}
								</Button>
							</Group>
						</>
					) : (
						<Alert
							variant="light"
							color="error"
							title={t(
								'resetPassword.passwordResetExpiredLinkIssue'
							)}
							icon={icon}
						>
							{t(
								'resetPassword.passwordResetExpiredLinkIssueDescription'
							)}
						</Alert>
					)
				) : (
					<Alert
						variant="light"
						color="error"
						title={t(
							'resetPassword.passwordResetLinkIssue'
						)}
						icon={icon}
					>
						{t(
							'resetPassword.passwordResetLinkIncorrect'
						)}
					</Alert>
				)}
			</FormWrapper>
		</form>
	);
};
